import React, { useState, useEffect, useCallback } from "react";
import { fetchApi } from "../utils/api";
import "../style/Notification-dropdown.css";
import NotificationInfo from "../pages/admin/NotificationInfo";

const Notification = ({ notifications, loading, refreshNotifications, onClose }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDropdown, setShowDropdown] = useState(true);

  // Deteksi ukuran layar
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cegah scroll background saat mobile
  useEffect(() => {
    if (isMobile && !selectedNotification) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, selectedNotification]);

  // Fungsi untuk menutup modal
  const handleCloseModal = () => {
    setSelectedNotification(null);
    setShowDropdown(true);
  };

  // Handler untuk klik notifikasi
  const handleNotificationClick = async (notif, e) => {
    e.stopPropagation();
    
    setShowDropdown(false);
    setSelectedNotification(notif);
    
    if (!notif.is_read) {
      try {
        await fetchApi(`/notifications/${notif.id}/read`, {
          method: "PATCH",
        });
        refreshNotifications();
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetchApi("/notifications/read-all", {
        method: "PATCH",
      });
      refreshNotifications();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  // Handle click overlay - hanya untuk mobile
  const handleOverlayClick = useCallback((e) => {
    // Hanya menutup jika target yang diklik adalah overlay itu sendiri
    if (e.target === e.currentTarget) {
      if (selectedNotification) {
        handleCloseModal();
      } else {
        onClose();
      }
    }
  }, [onClose, selectedNotification]);

  // Format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    if (date > oneWeekAgo) {
      return date.toLocaleString("en-US", { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    
    return date.toLocaleString("en-US", {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <>
        {isMobile && <div className="notification-overlay" onClick={handleOverlayClick} />}
        <div className="notification-dropdown">
          <div className="notification-container">
            <div className="notification-header">
              <h2>Notification</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="mark-read-btn" onClick={handleMarkAllRead}>
                  Mark All as Read
                </button>
                <button className="close-btn" onClick={onClose} aria-label="Close notifications">
                  ×
                </button>
              </div>
            </div>
            <p style={{ padding: "12px", color: "#777" }}>Loading...</p>
          </div>
        </div>

        {/* Modal untuk detail notifikasi */}
        {selectedNotification && (
          <div className="modal-overlay-navbar" onClick={handleOverlayClick}>
            <div 
              className="modal-content-notif-navbar" 
              onClick={(e) => {
                // Hentikan propagasi event untuk mencegah modal tertutup saat klik di dalam modal
                e.stopPropagation();
              }}
            >
              <NotificationInfo
                notification={selectedNotification}
                onClose={handleCloseModal}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Empty state
  if (!notifications || notifications.length === 0) {
    return (
      <>
        {isMobile && <div className="notification-overlay" onClick={handleOverlayClick} />}
        <div className="notification-dropdown">
          <div className="notification-container">
            <div className="notification-header">
              <h2>Notification</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="mark-read-btn" onClick={handleMarkAllRead}>
                  Mark All as Read
                </button>
                <button className="close-btn" onClick={onClose} aria-label="Close notifications">
                  ×
                </button>
              </div>
            </div>
            <p style={{ padding: "12px", color: "#777" }}>No notifications</p>
          </div>
        </div>

        {/* Modal untuk detail notifikasi */}
        {selectedNotification && (
          <div className="modal-overlay-navbar" onClick={handleOverlayClick}>
            <div 
              className="modal-content-notif-navbar" 
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <NotificationInfo
                notification={selectedNotification}
                onClose={handleCloseModal}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Main render
  return (
    <>
      {/* Overlay hanya untuk mobile - sembunyikan jika modal terbuka */}
      {isMobile && !selectedNotification && (
        <div 
          className="notification-overlay" 
          onClick={handleOverlayClick}
        />
      )}
      
      {/* Dropdown notifikasi - tampilkan jika showDropdown true */}
      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-container">
            <div className="notification-header">
              <h2>Notification</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button 
                  className="mark-read-btn" 
                  onClick={handleMarkAllRead}
                >
                  Mark All as Read
                </button>
              </div>
            </div>

            <div className="notification-list">
              {notifications.map((notif) => (
                <div 
                  key={notif.id} 
                  className={`notification-item ${notif.is_read ? "" : "unread"}`}
                  onClick={(e) => handleNotificationClick(notif, e)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleNotificationClick(notif, e);
                    }
                  }}
                >
                  <div className="notification-content">
                    <h3 className="notification-title">{notif.title}</h3>
                    <p className="notification-message">
                      {notif.content.length > 100 
                        ? notif.content.substring(0, 100) + '...' 
                        : notif.content}
                    </p>
                    <span className="notification-time">
                      {formatDate(notif.created_at)}
                    </span>
                  </div>
                  {!notif.is_read && <span className="unread-dot" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal untuk detail notifikasi */}
      {selectedNotification && (
        <div className="modal-overlay-navbar" onClick={handleOverlayClick}>
          <div 
            className="modal-content-notif-navbar" 
            onClick={(e) => {
              // Hentikan propagasi event untuk mencegah modal tertutup saat klik di dalam modal
              e.stopPropagation();
            }}
          >
            <NotificationInfo
              notification={selectedNotification}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;